import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { CheckCircle2, AlertCircle, MailCheck } from "lucide-react";

export default function VerifyEmail() {
    const [params] = useSearchParams();
    const { refreshUser } = useAuth();
    const navigate = useNavigate();
    const token = params.get("token");
    const [state, setState] = useState(token ? "verifying" : "idle"); // idle | verifying | success | error
    const [error, setError] = useState("");

    useEffect(() => {
        if (!token) return;
        (async () => {
            try {
                await api.post("/auth/verify-email", { token });
                if (refreshUser) await refreshUser();
                setState("success");
            } catch (err) {
                setError(err?.response?.data?.detail || "This verification link is invalid or expired.");
                setState("error");
            }
        })();
    }, [token, refreshUser]);

    return (
        <Shell>
            <div className="max-w-xl mx-auto px-6 py-20 text-center">
                {state === "verifying" && (
                    <>
                        <div className="inline-flex h-16 w-16 rounded-full bg-brand-mint items-center justify-center"><MailCheck size={28} className="text-brand-forest" /></div>
                        <h1 className="font-heading text-3xl font-extrabold mt-6">Verifying your email…</h1>
                        <p className="text-brand-mute mt-2">One moment.</p>
                    </>
                )}
                {state === "success" && (
                    <div data-testid="verify-email-success">
                        <div className="inline-flex h-16 w-16 rounded-full bg-emerald-100 items-center justify-center"><CheckCircle2 size={28} className="text-emerald-700" /></div>
                        <h1 className="font-heading text-3xl font-extrabold mt-6">Email verified!</h1>
                        <p className="text-brand-mute mt-2">You can now apply to events, host fairs, and receive booking notifications.</p>
                        <button onClick={() => navigate("/")} className="pill btn-coral px-6 py-3 mt-8 font-semibold">Continue</button>
                    </div>
                )}
                {state === "error" && (
                    <div data-testid="verify-email-error">
                        <div className="inline-flex h-16 w-16 rounded-full bg-brand-coral/15 items-center justify-center"><AlertCircle size={28} className="text-brand-coral" /></div>
                        <h1 className="font-heading text-3xl font-extrabold mt-6">Verification failed</h1>
                        <p className="text-brand-mute mt-2">{error}</p>
                        <Link to="/" className="pill px-6 py-3 mt-8 inline-block font-semibold border border-brand-line bg-white hover:bg-brand-bone">Back home</Link>
                    </div>
                )}
                {state === "idle" && (
                    <>
                        <h1 className="font-heading text-3xl font-extrabold mt-4">Check your email</h1>
                        <p className="text-brand-mute mt-3">We sent you a verification link. Click it to activate your account.</p>
                        <Link to="/" className="pill px-6 py-3 mt-8 inline-block font-semibold border border-brand-line bg-white hover:bg-brand-bone">Back home</Link>
                    </>
                )}
            </div>
        </Shell>
    );
}
